// The app's state, ported from the design's DCLogic class.
//
// One hook rather than a store: every piece of this state is read by the whole
// screen at once (changing the seed re-scores every card), so splitting it
// would just mean fan-out with extra ceremony.

import { useCallback, useEffect, useMemo, useState } from "react";

import { buildBoards, PRESETS, rank, scoreWork, whyShort } from "../data/engine";
import { recommendSimilar } from "../data/recommend";
import { searchWorks } from "../data/search";
import { loadCatalog } from "../data/source";
import type {
  Filters, LibraryState, Preset, Recommendation, SortKey, SourceMode, Work,
} from "../data/types";

export type Section = "discover" | "library" | "boards" | "identify";
export type Kind = "anime" | "manga";

const EMPTY_FILTERS: Filters = { genres: [], moods: [], minScore: 0, lens: [], statuses: [], query: "" };

/** localStorage with a cookie fallback, matching the design's persistence. */
function readStored(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v != null) return v;
  } catch { /* private mode — fall through */ }
  return (document.cookie.match("(^|; )" + key + "=([^;]*)") || [])[2] ?? null;
}

function writeStored(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
  try { document.cookie = `${key}=${value};path=/;max-age=31536000`; } catch { /* ignore */ }
}

/** Fixture-mode "similar", scored against the selected work rather than the seed. */
function localSimilar(catalog: Work[], to: Work, weights: Preset["weights"]): Recommendation[] {
  return catalog
    .filter((c) => c.kind === to.kind && c.id !== to.id)
    .map((c) => ({ ...c, ...scoreWork(c, to, weights) }))
    .sort((a, b) => b.match - a.match);
}

export function useKagami() {
  const [catalog, setCatalog] = useState<Work[]>([]);
  const [mode, setMode] = useState<SourceMode>("fixture");
  const [reason, setReason] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  const [kind, setKind] = useState<Kind>("anime");
  const [section, setSection] = useState<Section>("discover");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortKey>("match");
  const [preset, setPreset] = useState("balanced");
  const [seedId, setSeedId] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [library, setLibrary] = useState<Record<string, LibraryState>>({});
  const [shelf, setShelf] = useState("All");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setTheme] = useState("dark");
  const [density, setDensity] = useState("comfortable");
  // Server-side search results. null means "not searching"; the catalog page
  // is shown instead.
  const [searchHits, setSearchHits] = useState<Work[] | null>(null);
  const [searchError, setSearchError] = useState<string | undefined>();
  const [searching, setSearching] = useState(false);
  // Server-scored recommendations. null means "use the local engine", which
  // is the fixture-mode path.
  const [serverRecs, setServerRecs] = useState<Recommendation[] | null>(null);
  const [recError, setRecError] = useState<string | undefined>();
  // "Similar to the SELECTED work", scored server-side against that work
  // rather than against the seed.
  const [serverSimilar, setServerSimilar] = useState<Recommendation[] | null>(null);

  // ── load persisted prefs + the catalog ────────────────────────────────────
  useEffect(() => {
    const t = readStored("kagami_theme") || "dark";
    const d = readStored("kagami_density") || "comfortable";
    setTheme(t);
    setDensity(d);
    try {
      const raw = readStored("kagami_lib");
      if (raw) setLibrary(JSON.parse(decodeURIComponent(raw)) || {});
    } catch { /* corrupt blob — start empty rather than crash the app */ }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadCatalog(ac.signal)
      .then(({ mode, works, reason }) => {
        setCatalog(works);
        setMode(mode);
        setReason(reason);
        const stored = readStored("kagami_seed");
        setSeedId(stored && works.some((w) => w.id === stored) ? stored : works[0]?.id ?? null);
        setLoading(false);
      })
      .catch(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, []);

  // Theme and density are attributes on <html>, so CSS does the work and no
  // component needs to know about them.
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); }, [theme]);
  useEffect(() => { document.documentElement.setAttribute("data-density", density); }, [density]);

  // Query the server rather than filtering the loaded page: the catalog is
  // ~41k works and the client holds a few hundred, so a local filter would
  // only ever search the current page and look like a broken search.
  useEffect(() => {
    const q = filters.query.trim();
    if (!q) {
      setSearchHits(null);
      setSearchError(undefined);
      return;
    }
    // In fixture mode there is no backend to ask, and the 42-title catalog is
    // small enough that the local filter is genuinely correct.
    if (mode === "fixture") return;

    const ac = new AbortController();
    // Debounced: firing per keystroke would send a request for every prefix
    // of the query and race their responses.
    const t = setTimeout(() => {
      setSearching(true);
      searchWorks(q, kind, filters, ac.signal)
        .then(({ works, error }) => {
          setSearchHits(works);
          setSearchError(error);
        })
        .catch(() => { /* aborted */ })
        .finally(() => setSearching(false));
    }, 220);

    return () => { clearTimeout(t); ac.abort(); };
  }, [filters, kind, mode]);

  // Ask the server to score against the seed. The client only holds a page,
  // so a local scorer ranks a few hundred works while the server ranks every
  // candidate sharing a tag with the seed — tens of thousands.
  useEffect(() => {
    if (mode === "fixture" || !seedId || filters.query.trim()) {
      setServerRecs(null);
      setRecError(undefined);
      return;
    }
    const ac = new AbortController();
    recommendSimilar(seedId, preset, kind, filters, 60, ac.signal)
      .then(({ recommendations, error }) => {
        setServerRecs(recommendations);
        setRecError(error);
      })
      .catch(() => { /* aborted */ });
    return () => ac.abort();
  }, [mode, seedId, preset, kind, filters]);

  // ── derived ───────────────────────────────────────────────────────────────
  const seed = useMemo(
    () => catalog.find((c) => c.id === seedId) ?? catalog.find((c) => c.kind === kind) ?? catalog[0],
    [catalog, seedId, kind],
  );
  const presetObj = useMemo(() => PRESETS.find((p) => p.key === preset) ?? PRESETS[0], [preset]);

  const results: Recommendation[] = useMemo(() => {
    // Server-scored recommendations already carry match and signals; they are
    // used verbatim so the ordering the server chose survives.
    if (serverRecs !== null && searchHits === null) return serverRecs;

    if (!seed) return [];
    const searchMode = searchHits !== null;
    const pool = searchHits ?? catalog;

    // Hits are still SCORED against the seed, so "why this matches your
    // taste" survives the switch from browsing to searching...
    const scored = rank(pool, seed, kind,
      searchMode ? { ...filters, query: "" } : filters,
      presetObj.weights, sort);

    // ...but scoring must not RE-ORDER them. The server already ranked by
    // relevance, and sorting by taste-match on top of that buried
    // "Shingeki no Kyojin" (4% match) under an unrelated "Shining Star" (7%)
    // for the query "shingeki". Someone who typed a title wants that title.
    // An explicit sort chip still overrides this.
    if (searchMode && sort === "match") {
      const order = new Map(pool.map((w, i) => [w.id, i]));
      return scored.slice().sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }
    return scored;
  }, [catalog, searchHits, serverRecs, seed, kind, filters, presetObj, sort]);

  const genreOptions = useMemo(
    () => [...new Set(catalog.filter((c) => c.kind === kind).flatMap((c) => c.genres))].sort(),
    [catalog, kind],
  );
  const moodOptions = useMemo(
    () => [...new Set(catalog.filter((c) => c.kind === kind).flatMap((c) => c.moods))].sort(),
    [catalog, kind],
  );

  const selected = useMemo(() => {
    if (!selId) return null;

    // Look the work up across EVERY pool it could have been opened from.
    // Searching only the loaded catalog page meant clicking a search result
    // or a lower-ranked recommendation silently did nothing — it appeared to
    // work only because popular titles happen to be in that page.
    const fromResults = results.find((r) => r.id === selId);
    const raw = fromResults
      ?? searchHits?.find((w) => w.id === selId)
      ?? catalog.find((c) => c.id === selId);
    if (!raw) return null;

    // Prefer the score the card showed. Recomputing locally produced a
    // DIFFERENT percentage for the same work (45% in the drawer against 67%
    // on the card), because the server scores against a far larger candidate
    // pool than the client holds.
    let match = fromResults?.match ?? 0;
    let signals = fromResults?.signals ?? [];
    let why = fromResults?.why ?? "";
    // Fixture mode has no server to defer to, so the local engine is correct
    // there — and it is also the fallback when a work was opened from search,
    // where nothing scored it against the seed.
    if (!fromResults && seed) {
      const local = scoreWork(raw, seed, presetObj.weights);
      match = local.match;
      signals = local.signals;
      why = whyShort(local.signals, raw, seed);
    }

    const similar = (serverSimilar ?? localSimilar(catalog, raw, presetObj.weights))
      .slice(0, 5)
      .map((r) => ({
        id: r.id, title: r.title, match: r.match,
        why: ("why" in r && r.why) ? r.why : whyShort(r.signals, r, raw),
      }));

    return { work: raw, match, signals, why, similar };
  }, [selId, results, searchHits, catalog, seed, presetObj, serverSimilar]);

  const libraryWorks = useMemo(
    () => Object.keys(library).map((id) => catalog.find((c) => c.id === id)).filter((c): c is Work => !!c),
    [library, catalog],
  );
  const shelfWorks = useMemo(
    () => (shelf === "All" ? libraryWorks : libraryWorks.filter((c) => library[c.id] === shelf)),
    [libraryWorks, library, shelf],
  );
  const boards = useMemo(() => buildBoards(catalog, kind), [catalog, kind]);

  // Fetch "similar to the selected work" from the server. The client holds a
  // page; the server ranks every candidate sharing a tag with it.
  useEffect(() => {
    if (!selId || mode === "fixture") {
      setServerSimilar(null);
      return;
    }
    const ac = new AbortController();
    recommendSimilar(selId, preset, kind, EMPTY_FILTERS, 5, ac.signal)
      .then(({ recommendations }) => setServerSimilar(recommendations))
      .catch(() => { /* aborted */ });
    return () => ac.abort();
  }, [selId, preset, kind, mode]);

  // ── actions ───────────────────────────────────────────────────────────────
  const switchKind = useCallback((k: Kind) => {
    setKind(k);
    // Genres and moods are per-kind vocabularies; carrying them across would
    // silently filter everything out.
    setFilters((f) => ({ ...f, genres: [], moods: [] }));
    setSelId(null);
    setSeedId(catalog.find((c) => c.kind === k)?.id ?? null);
  }, [catalog]);

  const toggleIn = useCallback(<K extends "genres" | "moods" | "lens" | "statuses">(key: K, value: string) => {
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
    }));
  }, []);

  const setLibraryState = useCallback((id: string, value: LibraryState) => {
    setLibrary((lib) => {
      const next = { ...lib };
      // Picking the state it already has clears it — the chips act as a
      // toggle group with no "none" option of their own.
      if (next[id] === value) delete next[id]; else next[id] = value;
      writeStored("kagami_lib", encodeURIComponent(JSON.stringify(next)));
      return next;
    });
  }, []);

  const tuneTo = useCallback((id: string) => {
    writeStored("kagami_seed", id);
    setSeedId(id);
    setSelId(null);
    setSection("discover");
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => { const next = t === "dark" ? "light" : "dark"; writeStored("kagami_theme", next); return next; });
  }, []);

  const toggleDensity = useCallback(() => {
    setDensity((d) => {
      const next = d === "compact" ? "comfortable" : "compact";
      writeStored("kagami_density", next);
      return next;
    });
  }, []);

  const resetProfile = useCallback(() => {
    for (const k of ["kagami_lib", "kagami_seed", "kagami_theme", "kagami_density"]) {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
      try { document.cookie = `${k}=;path=/;max-age=0`; } catch { /* ignore */ }
    }
    setLibrary({});
    setShelf("All");
    setTheme("dark");
    setDensity("comfortable");
    setProfileOpen(false);
  }, []);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const toggleWhy = useCallback((id: string) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  }, []);

  return {
    loading, mode, reason, catalog,
    searching, searchError, isSearching: searchHits !== null,
    recError, serverScored: serverRecs !== null,
    kind, switchKind,
    section, setSection,
    filters, setFilters, toggleIn, clearFilters,
    sort, setSort,
    preset, setPreset, presetObj,
    seed, results, genreOptions, moodOptions,
    selId, setSelId, selected, expanded, toggleWhy,
    library, libraryWorks, shelfWorks, shelf, setShelf, setLibraryState,
    boards, tuneTo,
    filtersOpen, setFiltersOpen, profileOpen, setProfileOpen,
    theme, toggleTheme, density, toggleDensity, resetProfile,
  };
}
